/**
 * Add or edit one procedure line, in a drawer (breakdowns tab redesign,
 * 2026-10). Every line field in one form: the procedure, coverage and status,
 * how often (with basis, units and ages), a limit shared with other codes, the
 * alternate benefit, the history dates and the note. The rules are the ones the
 * old in-table editor enforced (lib/planDetails.lineEditError).
 *
 * The category decides the line's section; left blank on a new line, the
 * server gives it the category a scrape of this carrier would. An existing
 * line's procedure can't change (the server keeps it): delete and add instead.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Button, Checkbox, DatePicker, Drawer, Input, Popconfirm, Select, message } from "antd";
import dayjs from "dayjs";
import { useDebounce } from "use-debounce";

import { controller as rootController } from "../../../../../controller";
import { controller } from "../../../controller";
import { describeApiError } from "../../../lib/apiError";
import {
  LINE_CHOICES, ageBoundsFromTiers, choiceLabel, describeAgeTiers, lineEditError, parseCodeList,
  statusFromPercent, tiersFromAgeBounds,
} from "../../../lib/planDetails";
import { PLAN_DETAIL_ROWS } from "../../../lib/planDetails";
import { procedureName, rowCode } from "../../../lib/procedureFamilies";
import { SECTION_ORDER } from "./sheetModel";
import "./breakdownSheet.css";

const CATEGORY_OPTIONS = SECTION_ORDER.map((c) => ({ value: c, label: c }));

/** A line's fields as the drawer edits them (blank for a new line). */
export function initialEdit(row) {
  const r = row || {};
  const det = r.procedure_code_details || {};
  const bounds = ageBoundsFromTiers(r.age_tiers);
  const codes = (r.shared_limit_codes || []).join(", ");
  return {
    procedure_code_id: det.id || r.procedure_code || null,
    procedure_label: det.procedure_code ? `${det.procedure_code}${det.description ? ` - ${det.description}` : ""}` : "",
    coverage_type: r.coverage_type || "",
    percentage_covered: r.percentage_covered != null ? String(r.percentage_covered) : "",
    coverage_status: r.coverage_status || "",
    alternate_benefit: r.alternate_benefit || "",
    max_occurrences: r.max_occurrences != null ? String(r.max_occurrences) : "",
    coverage_duration_months: r.coverage_duration_months != null && r.coverage_duration_months !== 999 ? String(r.coverage_duration_months) : "",
    isFrequencyUnlimited: r.coverage_duration_months === 999,
    frequency_basis: r.frequency_basis || "",
    limit_unit: r.limit_unit || "",
    limit_scope: r.limit_scope || "",
    age_min: bounds.min,
    age_max: bounds.max,
    shared_limit_codes_text: codes,
    shared_limit_codes_initial: codes,
    shared_limit_count: r.shared_limit_count != null ? String(r.shared_limit_count) : "",
    shared_limit_months: r.shared_limit_months != null ? String(r.shared_limit_months) : "",
    last_service_date: r.last_service_date || "",
    eligible_again_on: r.eligible_again_on || "",
    notes: r.notes || "",
  };
}

function wholeOrNull(value) {
  return value == null || String(value).trim() === "" ? null : parseInt(value, 10);
}

// Codes that carriers answer with one rule (Cleer gives one line for each;
// ProviderConnect answers 01101/01102 through 01103 and the ortho codes through
// 93331). Scaling units are not here: Green Shield's 11111 and 11112 differ.
export const SAME_RULE_GROUPS = [["01101", "01102", "01103"], ["80000", "93331", "93341"]];

/** The other lines of this breakdown that a carrier answers with this code's rule. */
export function sameRuleSiblings(code, rows, selfId) {
  const group = SAME_RULE_GROUPS.find((g) => g.indexOf(code) !== -1);
  if (!group) return [];
  return (rows || []).filter((r) => r && r.id !== selfId && rowCode(r) !== code && group.indexOf(rowCode(r)) !== -1);
}

/**
 * The body that gives a sibling line this line's rule: coverage, status, how
 * often, basis, units, scope, ages and shared limit. The sibling keeps its own
 * category, alternate answer, history dates and procedure; its note says where
 * the rule came from.
 */
export function sameRuleBody(sibling, edit, fromCode) {
  const own = String(sibling.notes || "").trim();
  const note = [`Same rule as ${fromCode}.`, /^same rule as/i.test(own) ? "" : own].filter(Boolean).join(" ");
  const det = sibling.procedure_code_details || {};
  return linePayload(sibling, Object.assign({}, edit, {
    procedure_code_id: det.id || sibling.procedure_code,
    coverage_type: sibling.coverage_type || "",
    alternate_benefit: sibling.alternate_benefit || "",
    last_service_date: sibling.last_service_date || "",
    eligible_again_on: sibling.eligible_again_on || "",
    notes: note,
    // Re-send the codes only when they differ from the sibling's own.
    shared_limit_codes_initial: (sibling.shared_limit_codes || []).join(", "),
  }));
}

const ORTHO_TERMS = ["ortho_payment", "ortho_initial_payment_pct"];
const isOrthoCode = (code) => /^(8|93)/.test(String(code || ""));

/**
 * The PATCH / POST body for an edited line. Every field may be blank: a count
 * without months (or months without a count) is saved as given. `null` only
 * when a frequency value isn't a number (lineEditError says so first).
 */
export function linePayload(row, edit) {
  let maxOccurrences = null;
  let months = null;
  if (edit.isFrequencyUnlimited) {
    months = 999;
  } else {
    maxOccurrences = wholeOrNull(edit.max_occurrences);
    months = wholeOrNull(edit.coverage_duration_months);
    if (Number.isNaN(maxOccurrences) || Number.isNaN(months)) return null;
  }
  const body = {
    procedure_code: edit.procedure_code_id,
    percentage_covered: String(edit.percentage_covered == null ? "" : edit.percentage_covered).trim() === "" ? null : edit.percentage_covered,
    max_occurrences: maxOccurrences,
    coverage_duration_months: months,
    notes: edit.notes,
    // No status picked: the % says it (above 0 Covered, 0 Not covered).
    coverage_status: statusFromPercent(edit.percentage_covered, edit.coverage_status),
    frequency_basis: edit.frequency_basis || "",
    limit_unit: edit.limit_unit || "",
    limit_scope: edit.limit_scope || "",
    alternate_benefit: edit.alternate_benefit || "",
    shared_limit_count: wholeOrNull(edit.shared_limit_count),
    shared_limit_months: wholeOrNull(edit.shared_limit_months),
    last_service_date: edit.last_service_date || null,
    eligible_again_on: edit.eligible_again_on || null,
  };
  // A blank category on a new line is left to the server (the carrier's map).
  if (edit.coverage_type) body.coverage_type = edit.coverage_type;
  // Codes a scraper stored go back untouched unless someone retyped them.
  if ((edit.shared_limit_codes_text || "") !== (edit.shared_limit_codes_initial || "")) {
    body.shared_limit_codes = parseCodeList(edit.shared_limit_codes_text).codes;
  }
  // Several age tiers (Sun Life's "Limits - 1 / 2") are shown, not edited.
  if (!row || ageBoundsFromTiers(row.age_tiers).editable) {
    body.age_tiers = tiersFromAgeBounds(row && row.age_tiers, edit.age_min, edit.age_max);
  }
  return body;
}

export default function LineEditDrawer({ open, row, breakdownId, policyId, patientId, policy, rows, onClose, onSaved, onDeleted }) {
  const adding = !row;
  const [edit, setEdit] = useState(() => initialEdit(row));
  const termsOf = () => {
    const p = policy || {};
    return { ortho_payment: p.ortho_payment && p.ortho_payment !== "unknown" ? p.ortho_payment : "", ortho_initial_payment_pct: p.ortho_initial_payment_pct == null ? "" : String(Number(p.ortho_initial_payment_pct)) };
  };
  const [terms, setTerms] = useState(termsOf);
  const [copyRule, setCopyRule] = useState(false);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [debounced] = useDebounce(search, 400);
  const [options, setOptions] = useState([]);

  useEffect(() => {
    if (open) { setEdit(initialEdit(row)); setSearch(""); setTerms(termsOf()); setCopyRule(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, row]);

  useEffect(() => {
    if (!open || !debounced) return undefined;
    let live = true;
    const digits = (debounced.match(/\d+/g) || []).join("");
    const words = debounced.replace(/\d+/g, "").replace(/^-+|-+$/g, "").trim();
    controller.getProcedure(0, { procedureCode: digits, procedureDescription: words }).then((res) => {
      if (!live || !res || res.status >= 250) return;
      setOptions((res.json || []).map((p) => ({
        value: p.id,
        label: `${p.procedure_code}${p.procedure_code_description ? ` - ${p.procedure_code_description}` : ""}`,
      })));
    }).catch(() => {});
    return () => { live = false; };
  }, [open, debounced]);

  const set = (field) => (value) => setEdit((prev) => Object.assign({}, prev, { [field]: value }));
  const setFromEvent = (field) => (e) => set(field)(e.target.value);
  const bounds = ageBoundsFromTiers(row && row.age_tiers);
  const code = row ? rowCode(row) : String(edit.procedure_label || "").split(" ")[0];
  const ortho = isOrthoCode(code);
  const siblings = sameRuleSiblings(code, rows, row && row.id);
  const impliedStatus = edit.coverage_status ? "" : statusFromPercent(edit.percentage_covered, "");
  const termsChanged = () => {
    const was = termsOf();
    const out = {};
    ORTHO_TERMS.forEach((f) => { if (terms[f] !== was[f]) out[f] = terms[f] === "" ? null : terms[f]; });
    return out;
  };
  const title = useMemo(() => {
    if (adding) return "Add a procedure";
    const n = procedureName(row);
    return `${n.name || n.code}${n.qualifier ? `, ${n.qualifier}` : ""} · ${n.code}`;
  }, [adding, row]);

  async function save() {
    if (!edit.procedure_code_id) { message.error("Choose the procedure first."); return; }
    const problem = lineEditError(edit);
    if (problem) { message.error(problem); return; }
    const body = linePayload(row, edit);
    if (!body) { message.error("How often takes whole numbers, or leave it blank."); return; }
    setSaving(true);
    try {
      const res = adding
        ? await controller.addNewBreakdownProcedure(Object.assign({
          insurance_breakdown_id: breakdownId || null,
          insurance_policy: policyId,
          patient_id: patientId,
        }, body))
        : await controller.updateBreakdown(breakdownId, Object.assign({ id: row.id }, body));
      if (res && res.status < 250) {
        const extra = [];
        // The ortho terms are the plan's: one answer, saved on the policy.
        const termChanges = ortho && policyId ? termsChanged() : {};
        if (Object.keys(termChanges).length) {
          const pr = await rootController.updatePatientInsurancePolicy(policyId, termChanges);
          if (!pr || pr.status !== 200) extra.push("the ortho terms");
        }
        // The same rule for the codes the carrier answers together, only when asked.
        if (copyRule && siblings.length) {
          const failed = [];
          for (const sib of siblings) {
            const sibBody = sameRuleBody(sib, edit, code);
            const sr = sibBody ? await controller.updateBreakdown(breakdownId || sib.insurance_breakdown, Object.assign({ id: sib.id }, sibBody)) : null;
            if (!sr || sr.status >= 250) failed.push(rowCode(sib));
          }
          if (failed.length) extra.push(`the rule for ${failed.join(", ")}`);
        }
        if (extra.length) message.warning(`Line saved, but not ${extra.join(" or ")}. Try again.`);
        else message.success(adding ? "Procedure added" : copyRule && siblings.length ? "Lines saved" : "Line saved");
        if (onSaved) onSaved(res.json, { created: adding });
      } else {
        message.error(describeApiError(res, adding ? "Couldn't add that procedure." : "Couldn't save the line."));
      }
    } catch (err) {
      message.error(adding ? "Couldn't add that procedure." : "Couldn't save the line.");
    } finally {
      setSaving(false);
    }
  }

  async function remove() {
    setSaving(true);
    try {
      const res = await controller.deleteBreakdownProcedure(breakdownId, row.id);
      if (res && res.status < 250) {
        message.success("Line deleted");
        if (onDeleted) onDeleted(row);
      } else {
        message.error(describeApiError(res, "Couldn't delete that line."));
      }
    } catch (err) {
      message.error("Couldn't delete that line.");
    } finally {
      setSaving(false);
    }
  }

  const popup = (node) => (node && node.parentNode) || document.body;

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={title}
      width={460}
      zIndex={1001}
      destroyOnClose
      footer={(
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          {!adding ? (
            <Popconfirm title="Delete this line from the breakdown?" okText="Delete" okButtonProps={{ danger: true }} onConfirm={remove} zIndex={1100}>
              <Button danger disabled={saving}>Delete</Button>
            </Popconfirm>
          ) : null}
          <span style={{ flex: 1 }} />
          <Button onClick={onClose} disabled={saving}>Cancel</Button>
          <Button type="primary" onClick={save} loading={saving}>{adding ? "Add" : "Save"}</Button>
        </div>
      )}
    >
      <div className="bs-form">
        <div className="grp">
          <span className="lbl">Procedure</span>
          <Select
            id="line-procedure"
            showSearch
            disabled={!adding}
            filterOption={false}
            placeholder="Search a code or a name"
            onSearch={setSearch}
            options={options.length ? options : (edit.procedure_code_id ? [{ value: edit.procedure_code_id, label: edit.procedure_label }] : [])}
            value={edit.procedure_code_id || undefined}
            onChange={(value, option) => setEdit((prev) => Object.assign({}, prev, { procedure_code_id: value, procedure_label: option && option.label }))}
            getPopupContainer={popup}
          />
          {!adding ? <div className="hint">To use a different procedure, delete this line and add that one.</div> : null}
          <Select id="line-category" aria-label="Category" allowClear placeholder="Category (from the carrier's map when blank)"
            options={CATEGORY_OPTIONS} value={edit.coverage_type || undefined}
            onChange={(v) => set("coverage_type")(v || "")} getPopupContainer={popup} />
        </div>

        <div className="grp">
          <span className="lbl">Covered</span>
          <div className="row2">
            <Input id="line-pct" aria-label="Coverage %" placeholder="Coverage %" value={edit.percentage_covered} onChange={setFromEvent("percentage_covered")} />
            <Select id="line-status" aria-label="Status" allowClear options={LINE_CHOICES.coverage_status}
              placeholder={impliedStatus ? `${choiceLabel(LINE_CHOICES.coverage_status, impliedStatus)} (from the %)` : "Status"}
              value={edit.coverage_status || undefined} onChange={(v) => set("coverage_status")(v || "")} getPopupContainer={popup} />
          </div>
          <Select id="line-alternate" aria-label="Alternate benefit" allowClear placeholder="Alternate benefit" options={LINE_CHOICES.alternate_benefit}
            value={edit.alternate_benefit || undefined} onChange={(v) => set("alternate_benefit")(v || "")} getPopupContainer={popup} />
        </div>

        <div className="grp">
          <span className="lbl">How often</span>
          <div className="inline">
            <Input id="line-count" aria-label="Occurrences" placeholder="Count" style={{ width: 80 }} disabled={edit.isFrequencyUnlimited}
              value={edit.isFrequencyUnlimited ? "" : edit.max_occurrences} onChange={setFromEvent("max_occurrences")} />
            <span>every</span>
            <Input id="line-months" aria-label="Months" placeholder="Months" style={{ width: 90 }} disabled={edit.isFrequencyUnlimited}
              value={edit.isFrequencyUnlimited ? "" : edit.coverage_duration_months} onChange={setFromEvent("coverage_duration_months")} />
            <span>months</span>
            <Checkbox checked={edit.isFrequencyUnlimited} onChange={(e) => set("isFrequencyUnlimited")(e.target.checked)}>No limit</Checkbox>
          </div>
          <div className="row2">
            <Select id="line-basis" aria-label="Basis" allowClear placeholder="Rolling or calendar?" options={LINE_CHOICES.frequency_basis}
              value={edit.frequency_basis || undefined} onChange={(v) => set("frequency_basis")(v || "")} getPopupContainer={popup} />
            <Select id="line-units" aria-label="Counts" allowClear placeholder="Counts" options={LINE_CHOICES.limit_unit}
              value={edit.limit_unit || undefined} onChange={(v) => set("limit_unit")(v || "")} getPopupContainer={popup} />
          </div>
          <Select id="line-scope" aria-label="Limited per" allowClear placeholder="Limited per (person, tooth or surface)" options={LINE_CHOICES.limit_scope}
            value={edit.limit_scope || undefined} onChange={(v) => set("limit_scope")(v || "")} getPopupContainer={popup} />
          {bounds.editable ? (
            <div className="inline">
              <span>Ages</span>
              <Input id="line-age-min" aria-label="From age" placeholder="from" style={{ width: 70 }} value={edit.age_min} onChange={setFromEvent("age_min")} />
              <span>to</span>
              <Input id="line-age-max" aria-label="To age" placeholder="to" style={{ width: 70 }} value={edit.age_max} onChange={setFromEvent("age_max")} />
            </div>
          ) : <div className="hint">{`Age rules from the carrier: ${describeAgeTiers(row.age_tiers)}`}</div>}
        </div>

        {ortho ? (
          <div className="grp">
            <span className="lbl">Ortho terms</span>
            <div className="row2">
              <Select id="line-ortho-payment" aria-label="Ortho payment" allowClear placeholder="How it's paid"
                options={(PLAN_DETAIL_ROWS.find((r) => r.field === "ortho_payment") || {}).options}
                value={terms.ortho_payment || undefined} getPopupContainer={popup}
                onChange={(v) => setTerms((prev) => Object.assign({}, prev, { ortho_payment: v || "" }))} />
              <Input id="line-ortho-initial" aria-label="Paid on the initial fee" placeholder="Paid on the initial fee" suffix="%"
                value={terms.ortho_initial_payment_pct}
                onChange={(e) => { const v = e.target.value; setTerms((prev) => Object.assign({}, prev, { ortho_initial_payment_pct: v })); }} />
            </div>
            <div className="hint">The plan's terms for every ortho code. The ages above set the plan's ortho ages.</div>
          </div>
        ) : null}

        <div className="grp">
          <span className="lbl">Shared limit</span>
          <Input id="line-shared-codes" aria-label="Shares its limit with" placeholder="Codes that share this limit, e.g. 01204, 01205"
            value={edit.shared_limit_codes_text} onChange={setFromEvent("shared_limit_codes_text")} />
          <div className="inline">
            <Input id="line-shared-count" aria-label="Combined count" placeholder="#" style={{ width: 70 }} value={edit.shared_limit_count} onChange={setFromEvent("shared_limit_count")} />
            <span>every</span>
            <Input id="line-shared-months" aria-label="Combined months" placeholder="months" style={{ width: 90 }} value={edit.shared_limit_months} onChange={setFromEvent("shared_limit_months")} />
            <span>months, combined</span>
          </div>
        </div>

        <div className="grp">
          <span className="lbl">History</span>
          <div className="row2">
            <DatePicker id="line-last-done" placeholder="Last done" format="YYYY-MM-DD" getPopupContainer={popup}
              value={edit.last_service_date ? dayjs(edit.last_service_date, "YYYY-MM-DD") : null}
              onChange={(d) => set("last_service_date")(d ? d.format("YYYY-MM-DD") : "")} />
            <DatePicker id="line-next" placeholder="Next eligible" format="YYYY-MM-DD" getPopupContainer={popup}
              value={edit.eligible_again_on ? dayjs(edit.eligible_again_on, "YYYY-MM-DD") : null}
              onChange={(d) => set("eligible_again_on")(d ? d.format("YYYY-MM-DD") : "")} />
          </div>
        </div>

        {siblings.length ? (
          <div className="grp">
            <Checkbox id="line-same-rule" checked={copyRule} onChange={(e) => setCopyRule(e.target.checked)}>
              {`Same rule for ${siblings.map(rowCode).join(" and ")}`}
            </Checkbox>
            <div className="hint">Copies the coverage, how often, ages and shared limit to those lines. Their history stays their own.</div>
          </div>
        ) : null}

        <div className="grp">
          <span className="lbl">Note</span>
          <Input.TextArea id="line-notes" aria-label="Note" autoSize={{ minRows: 2, maxRows: 8 }} value={edit.notes} onChange={setFromEvent("notes")}
            placeholder="What the rep or the portal said, word for word" />
        </div>
      </div>
    </Drawer>
  );
}
